import React, { useState } from "react";
import { api } from "@/lib/api";
import { Heart, Calculator, AlertCircle, Loader2, Activity, Shield, TrendingUp } from "lucide-react";
import { Field, Checkbox, ASCVD_COLOR, HEART_COLOR } from "./shared";

const BAND_COLOR = { Low: "#4ADE80", Borderline: "#FBBF24", Moderate: "#FB923C", Intermediate: "#FB923C", High: "#F87171" };
const URGENCY_COLOR = { routine: "#4ADE80", high: "#FB923C", urgent: "#F87171" };

export default function RiskTab({ latestBp }) {
  return (
    <div className="space-y-6" data-testid="cardio-risk-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AscvdCalculator prefillSbp={latestBp?.systolic} />
        <HeartCalculator />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChadsVascCalculator />
        <HasBledCalculator />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TimiCalculator />
        <Qrisk3Calculator prefillSbp={latestBp?.systolic} />
      </div>
      <AfibWorkflow />
    </div>
  );
}

/* ---------------- ASCVD (unchanged) ---------------- */
function AscvdCalculator({ prefillSbp }) {
  const [form, setForm] = useState({
    sex: "male", race: "white", age: 55,
    total_cholesterol: 200, hdl: 45,
    systolic_bp: prefillSbp || 130,
    on_bp_treatment: false, smoker: false, diabetes: false,
  });
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState("");

  async function compute() {
    setError(""); setResult(null); setComputing(true);
    try {
      const { data } = await api.post("/specialties/cardiology/scores/ascvd", {
        ...form,
        age: Number(form.age),
        total_cholesterol: Number(form.total_cholesterol),
        hdl: Number(form.hdl),
        systolic_bp: Number(form.systolic_bp),
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to compute ASCVD risk");
    } finally { setComputing(false); }
  }

  const bg = result ? (ASCVD_COLOR[result.category] || "#A3ACA7") : "#A3ACA7";

  return (
    <div className="ros-card space-y-4" data-testid="ascvd-calculator">
      <div className="flex items-center gap-2">
        <Calculator size={16} className="text-[#F87171]" />
        <div className="font-heading text-lg">ASCVD 10-year risk</div>
      </div>
      <div className="text-xs text-[#A3ACA7]">
        ACC/AHA Pooled Cohort Equations (2013). Validated for ages 40–79.
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Sex">
          <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}
                  data-testid="ascvd-sex" className="ros-input">
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </Field>
        <Field label="Race">
          <select value={form.race} onChange={(e) => setForm({ ...form, race: e.target.value })}
                  data-testid="ascvd-race" className="ros-input">
            <option value="white">White</option>
            <option value="black">Black</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Age (40–79)">
          <input type="number" min={40} max={79} value={form.age}
                 onChange={(e) => setForm({ ...form, age: e.target.value })}
                 data-testid="ascvd-age" className="ros-input" />
        </Field>
        <Field label="Total chol (mg/dL)">
          <input type="number" step="0.1" value={form.total_cholesterol}
                 onChange={(e) => setForm({ ...form, total_cholesterol: e.target.value })}
                 data-testid="ascvd-tc" className="ros-input" />
        </Field>
        <Field label="HDL (mg/dL)">
          <input type="number" step="0.1" value={form.hdl}
                 onChange={(e) => setForm({ ...form, hdl: e.target.value })}
                 data-testid="ascvd-hdl" className="ros-input" />
        </Field>
        <Field label="Systolic BP (mmHg)">
          <input type="number" value={form.systolic_bp}
                 onChange={(e) => setForm({ ...form, systolic_bp: e.target.value })}
                 data-testid="ascvd-sbp" className="ros-input" />
        </Field>
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        <Checkbox label="On BP treatment" checked={form.on_bp_treatment}
                  onChange={(v) => setForm({ ...form, on_bp_treatment: v })} testId="ascvd-bp-tx" />
        <Checkbox label="Smoker" checked={form.smoker}
                  onChange={(v) => setForm({ ...form, smoker: v })} testId="ascvd-smoker" />
        <Checkbox label="Diabetes" checked={form.diabetes}
                  onChange={(v) => setForm({ ...form, diabetes: v })} testId="ascvd-diabetes" />
      </div>

      <div>
        <button onClick={compute} disabled={computing} data-testid="ascvd-compute"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
          {computing ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
          {computing ? "Computing…" : "Compute risk"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="ascvd-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="rounded-2xl p-4"
             style={{ background: `${bg}12`, border: `1px solid ${bg}40` }}
             data-testid="ascvd-result">
          <div className="text-4xl font-heading font-bold" style={{ color: bg }}>
            {result.risk_percent}<span className="text-xl text-[#A3ACA7] ml-1">%</span>
          </div>
          <div className="mt-1 text-sm font-semibold" style={{ color: bg }}>
            {result.category} risk
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- HEART (unchanged) ---------------- */
function HeartCalculator() {
  const [form, setForm] = useState({
    history: "slightly_suspicious", ekg: "normal", age: 55,
    risk_factors_count: 1, troponin_multiple_of_normal: 1,
  });
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState("");

  async function compute() {
    setError(""); setResult(null); setComputing(true);
    try {
      const { data } = await api.post("/specialties/cardiology/scores/heart", {
        history: form.history, ekg: form.ekg, age: Number(form.age),
        risk_factors_count: Number(form.risk_factors_count),
        troponin_multiple_of_normal: Number(form.troponin_multiple_of_normal),
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to compute HEART Score");
    } finally { setComputing(false); }
  }

  const bg = result ? (HEART_COLOR[result.risk] || "#A3ACA7") : "#A3ACA7";

  return (
    <div className="ros-card space-y-4" data-testid="heart-calculator">
      <div className="flex items-center gap-2">
        <Heart size={16} className="text-[#F87171]" />
        <div className="font-heading text-lg">HEART Score</div>
      </div>
      <div className="text-xs text-[#A3ACA7]">ED chest-pain 6-week MACE risk. 0–3 discharge · 4–6 observe · 7–10 admit.</div>

      <Field label="History suspicion">
        <select value={form.history} onChange={(e) => setForm({ ...form, history: e.target.value })}
                data-testid="heart-history" className="ros-input">
          <option value="slightly_suspicious">Slightly suspicious</option>
          <option value="moderately_suspicious">Moderately suspicious</option>
          <option value="highly_suspicious">Highly suspicious</option>
        </select>
      </Field>
      <Field label="EKG findings">
        <select value={form.ekg} onChange={(e) => setForm({ ...form, ekg: e.target.value })}
                data-testid="heart-ekg" className="ros-input">
          <option value="normal">Normal</option>
          <option value="nonspecific_repolarization">Non-specific repolarization</option>
          <option value="significant_st_deviation">Significant ST deviation</option>
        </select>
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Age">
          <input type="number" min={18} max={110} value={form.age}
                 onChange={(e) => setForm({ ...form, age: e.target.value })}
                 data-testid="heart-age" className="ros-input" />
        </Field>
        <Field label="Risk factors">
          <input type="number" min={0} max={7} value={form.risk_factors_count}
                 onChange={(e) => setForm({ ...form, risk_factors_count: e.target.value })}
                 data-testid="heart-rf" className="ros-input" />
        </Field>
        <Field label="Troponin × normal">
          <input type="number" step="0.1" min={0} value={form.troponin_multiple_of_normal}
                 onChange={(e) => setForm({ ...form, troponin_multiple_of_normal: e.target.value })}
                 data-testid="heart-troponin" className="ros-input" />
        </Field>
      </div>

      <div>
        <button onClick={compute} disabled={computing} data-testid="heart-compute"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
          {computing ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
          {computing ? "Computing…" : "Compute HEART"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="heart-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="rounded-2xl p-4"
             style={{ background: `${bg}12`, border: `1px solid ${bg}40` }}
             data-testid="heart-result">
          <div className="text-4xl font-heading font-bold" style={{ color: bg }}>
            {result.total}<span className="text-xl text-[#A3ACA7] ml-1">/ 10</span>
          </div>
          <div className="mt-1 text-sm font-semibold" style={{ color: bg }}>{result.risk} risk</div>
          <div className="text-sm text-white mt-2">{result.recommendation}</div>
        </div>
      )}
    </div>
  );
}

/* ---------------- CHA₂DS₂-VASc ---------------- */
function ChadsVascCalculator() {
  const [form, setForm] = useState({
    age: 68, sex: "male",
    chf: false, htn: false, diabetes: false,
    stroke: false, vascular: false,
  });
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState("");

  async function compute() {
    setError(""); setResult(null); setComputing(true);
    try {
      const { data } = await api.post("/specialties/cardiology/scores/chadsvasc", {
        ...form, age: Number(form.age),
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to compute CHA₂DS₂-VASc");
    } finally { setComputing(false); }
  }

  const band = result ? (result.score <= 1 ? "Low" : result.score <= 3 ? "Moderate" : "High") : null;
  const bg = band ? BAND_COLOR[band] : "#A3ACA7";

  return (
    <div className="ros-card space-y-4" data-testid="chadsvasc-calculator">
      <div className="flex items-center gap-2">
        <Shield size={16} className="text-[#F87171]" />
        <div className="font-heading text-lg">CHA₂DS₂-VASc</div>
      </div>
      <div className="text-xs text-[#A3ACA7]">AFib stroke-risk stratification. ≥1 (M) / ≥2 (F) → anticoagulate.</div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Age">
          <input type="number" min={18} max={110} value={form.age}
                 onChange={(e) => setForm({ ...form, age: e.target.value })}
                 data-testid="chadsvasc-age" className="ros-input" />
        </Field>
        <Field label="Sex">
          <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}
                  data-testid="chadsvasc-sex" className="ros-input">
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </Field>
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        <Checkbox label="CHF / LVEF ≤40%" checked={form.chf}
                  onChange={(v) => setForm({ ...form, chf: v })} testId="chadsvasc-chf" />
        <Checkbox label="Hypertension" checked={form.htn}
                  onChange={(v) => setForm({ ...form, htn: v })} testId="chadsvasc-htn" />
        <Checkbox label="Diabetes" checked={form.diabetes}
                  onChange={(v) => setForm({ ...form, diabetes: v })} testId="chadsvasc-dm" />
        <Checkbox label="Prior stroke/TIA (2 pts)" checked={form.stroke}
                  onChange={(v) => setForm({ ...form, stroke: v })} testId="chadsvasc-stroke" />
        <Checkbox label="Vascular disease" checked={form.vascular}
                  onChange={(v) => setForm({ ...form, vascular: v })} testId="chadsvasc-vasc" />
      </div>

      <div>
        <button onClick={compute} disabled={computing} data-testid="chadsvasc-compute"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
          {computing ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
          {computing ? "Computing…" : "Compute stroke risk"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="chadsvasc-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="rounded-2xl p-4" style={{ background: `${bg}12`, border: `1px solid ${bg}40` }}
             data-testid="chadsvasc-result">
          <div className="text-4xl font-heading font-bold" style={{ color: bg }}>
            {result.score}<span className="text-xl text-[#A3ACA7] ml-1">/ {result.max_score}</span>
          </div>
          <div className="mt-1 text-sm font-semibold" style={{ color: bg }}>
            Annual stroke risk: {result.annual_stroke_risk}
          </div>
          <div className="text-sm text-white mt-2">{result.recommendation}</div>
        </div>
      )}
    </div>
  );
}

/* ---------------- HAS-BLED ---------------- */
function HasBledCalculator() {
  const [form, setForm] = useState({
    hypertension_uncontrolled: false, renal_disease: false, liver_disease: false,
    prior_stroke: false, prior_bleeding: false, labile_inr: false,
    age_over_65: false, drugs_predisposing: false, alcohol_use: false,
  });
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState("");

  async function compute() {
    setError(""); setResult(null); setComputing(true);
    try {
      const { data } = await api.post("/specialties/cardiology/scores/hasbled", form);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to compute HAS-BLED");
    } finally { setComputing(false); }
  }

  const bg = result ? BAND_COLOR[result.risk_band] : "#A3ACA7";
  const items = [
    ["hypertension_uncontrolled", "Hypertension (uncontrolled SBP >160)"],
    ["renal_disease", "Renal disease (Cr >2.3 or dialysis)"],
    ["liver_disease", "Liver disease"],
    ["prior_stroke", "Prior stroke"],
    ["prior_bleeding", "Prior major bleeding"],
    ["labile_inr", "Labile INR (TTR <60%)"],
    ["age_over_65", "Age > 65"],
    ["drugs_predisposing", "Drugs (NSAIDs/antiplatelets)"],
    ["alcohol_use", "Alcohol ≥ 8 units/wk"],
  ];

  return (
    <div className="ros-card space-y-4" data-testid="hasbled-calculator">
      <div className="flex items-center gap-2">
        <AlertCircle size={16} className="text-[#F87171]" />
        <div className="font-heading text-lg">HAS-BLED</div>
      </div>
      <div className="text-xs text-[#A3ACA7]">Bleeding risk on anticoagulation. Pairs with CHA₂DS₂-VASc.</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        {items.map(([k, label]) => (
          <Checkbox key={k} label={label} checked={form[k]}
                    onChange={(v) => setForm({ ...form, [k]: v })}
                    testId={`hasbled-${k}`} />
        ))}
      </div>

      <div>
        <button onClick={compute} disabled={computing} data-testid="hasbled-compute"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
          {computing ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
          {computing ? "Computing…" : "Compute bleeding risk"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="hasbled-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="rounded-2xl p-4" style={{ background: `${bg}12`, border: `1px solid ${bg}40` }}
             data-testid="hasbled-result">
          <div className="text-4xl font-heading font-bold" style={{ color: bg }}>
            {result.score}<span className="text-xl text-[#A3ACA7] ml-1">/ {result.max_score}</span>
          </div>
          <div className="mt-1 text-sm font-semibold" style={{ color: bg }}>{result.risk_band} bleeding risk</div>
          <div className="text-sm text-white mt-2">{result.recommendation}</div>
        </div>
      )}
    </div>
  );
}

/* ---------------- TIMI ---------------- */
function TimiCalculator() {
  const [form, setForm] = useState({
    age_over_65: false, three_cad_risk_factors: false, known_cad_50: false,
    aspirin_last_7d: false, severe_angina_24h: false,
    st_deviation: false, elevated_biomarker: false,
  });
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState("");

  async function compute() {
    setError(""); setResult(null); setComputing(true);
    try {
      const { data } = await api.post("/specialties/cardiology/scores/timi", form);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to compute TIMI Score");
    } finally { setComputing(false); }
  }

  const bg = result ? BAND_COLOR[result.risk_band] : "#A3ACA7";
  const items = [
    ["age_over_65", "Age ≥ 65"],
    ["three_cad_risk_factors", "≥3 CAD risk factors"],
    ["known_cad_50", "Known CAD (stenosis ≥50%)"],
    ["aspirin_last_7d", "Aspirin use in past 7 d"],
    ["severe_angina_24h", "Severe angina in past 24 h"],
    ["st_deviation", "ST-segment deviation ≥ 0.5 mm"],
    ["elevated_biomarker", "Elevated cardiac biomarker"],
  ];

  return (
    <div className="ros-card space-y-4" data-testid="timi-calculator">
      <div className="flex items-center gap-2">
        <Activity size={16} className="text-[#F87171]" />
        <div className="font-heading text-lg">TIMI (UA/NSTEMI)</div>
      </div>
      <div className="text-xs text-[#A3ACA7]">14-day death/MI/revascularisation risk — invasive-strategy decision.</div>

      <div className="grid grid-cols-1 gap-2 text-sm">
        {items.map(([k, label]) => (
          <Checkbox key={k} label={label} checked={form[k]}
                    onChange={(v) => setForm({ ...form, [k]: v })}
                    testId={`timi-${k}`} />
        ))}
      </div>

      <div>
        <button onClick={compute} disabled={computing} data-testid="timi-compute"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
          {computing ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
          {computing ? "Computing…" : "Compute TIMI"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="timi-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="rounded-2xl p-4" style={{ background: `${bg}12`, border: `1px solid ${bg}40` }}
             data-testid="timi-result">
          <div className="text-4xl font-heading font-bold" style={{ color: bg }}>
            {result.score}<span className="text-xl text-[#A3ACA7] ml-1">/ {result.max_score}</span>
          </div>
          <div className="mt-1 text-sm font-semibold" style={{ color: bg }}>
            {result.risk_band} risk · 14-d event {result.event_risk_14d}
          </div>
          <div className="text-sm text-white mt-2">{result.recommendation}</div>
        </div>
      )}
    </div>
  );
}

/* ---------------- QRISK3 ---------------- */
function Qrisk3Calculator({ prefillSbp }) {
  const [form, setForm] = useState({
    age: 55, sex: "male",
    systolic_bp: prefillSbp || 130,
    tc_hdl_ratio: 4.0,
    bmi: 26,
    smoker_level: 0,
    diabetes_t1: false, diabetes_t2: false,
    family_hx_cvd: false, ckd_stage_3_5: false,
    atrial_fibrillation: false, on_bp_treatment: false,
    migraine: false, rheumatoid_arthritis: false, sle: false,
    severe_mental_illness: false, atypical_antipsychotics: false,
    oral_corticosteroids: false, erectile_dysfunction: false,
  });
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState("");

  async function compute() {
    setError(""); setResult(null); setComputing(true);
    try {
      const { data } = await api.post("/specialties/cardiology/scores/qrisk3", {
        ...form,
        age: Number(form.age),
        systolic_bp: Number(form.systolic_bp),
        tc_hdl_ratio: Number(form.tc_hdl_ratio),
        bmi: Number(form.bmi),
        smoker_level: Number(form.smoker_level),
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to compute QRISK3");
    } finally { setComputing(false); }
  }

  const band = result ? BAND_COLOR[result.risk_band] : "#A3ACA7";

  return (
    <div className="ros-card space-y-4" data-testid="qrisk3-calculator">
      <div className="flex items-center gap-2">
        <TrendingUp size={16} className="text-[#F87171]" />
        <div className="font-heading text-lg">QRISK3 10-yr CVD (UK)</div>
      </div>
      <div className="text-xs text-[#A3ACA7]">NICE-recommended UK primary-care 10-yr CVD risk. Validated ages 25–84.</div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Age (25–84)">
          <input type="number" min={25} max={84} value={form.age}
                 onChange={(e) => setForm({ ...form, age: e.target.value })}
                 data-testid="qrisk3-age" className="ros-input" />
        </Field>
        <Field label="Sex">
          <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}
                  data-testid="qrisk3-sex" className="ros-input">
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </Field>
        <Field label="Systolic BP (mmHg)">
          <input type="number" value={form.systolic_bp}
                 onChange={(e) => setForm({ ...form, systolic_bp: e.target.value })}
                 data-testid="qrisk3-sbp" className="ros-input" />
        </Field>
        <Field label="TC / HDL ratio">
          <input type="number" step="0.1" value={form.tc_hdl_ratio}
                 onChange={(e) => setForm({ ...form, tc_hdl_ratio: e.target.value })}
                 data-testid="qrisk3-ratio" className="ros-input" />
        </Field>
        <Field label="BMI (kg/m²)">
          <input type="number" step="0.1" value={form.bmi}
                 onChange={(e) => setForm({ ...form, bmi: e.target.value })}
                 data-testid="qrisk3-bmi" className="ros-input" />
        </Field>
        <Field label="Smoking">
          <select value={form.smoker_level}
                  onChange={(e) => setForm({ ...form, smoker_level: e.target.value })}
                  data-testid="qrisk3-smoker" className="ros-input">
            <option value={0}>Non-smoker</option>
            <option value={1}>Ex-smoker</option>
            <option value={2}>Light (&lt;10/day)</option>
            <option value={3}>Moderate (10–19/day)</option>
            <option value={4}>Heavy (≥20/day)</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        <Checkbox label="Type 1 diabetes" checked={form.diabetes_t1}
                  onChange={(v) => setForm({ ...form, diabetes_t1: v })} testId="qrisk3-dm1" />
        <Checkbox label="Type 2 diabetes" checked={form.diabetes_t2}
                  onChange={(v) => setForm({ ...form, diabetes_t2: v })} testId="qrisk3-dm2" />
        <Checkbox label="Family Hx CVD <60y" checked={form.family_hx_cvd}
                  onChange={(v) => setForm({ ...form, family_hx_cvd: v })} testId="qrisk3-famhx" />
        <Checkbox label="CKD stage 3–5" checked={form.ckd_stage_3_5}
                  onChange={(v) => setForm({ ...form, ckd_stage_3_5: v })} testId="qrisk3-ckd" />
        <Checkbox label="Atrial fibrillation" checked={form.atrial_fibrillation}
                  onChange={(v) => setForm({ ...form, atrial_fibrillation: v })} testId="qrisk3-af" />
        <Checkbox label="On BP treatment" checked={form.on_bp_treatment}
                  onChange={(v) => setForm({ ...form, on_bp_treatment: v })} testId="qrisk3-bpx" />
        <Checkbox label="Migraine" checked={form.migraine}
                  onChange={(v) => setForm({ ...form, migraine: v })} testId="qrisk3-migraine" />
        <Checkbox label="Rheumatoid arthritis" checked={form.rheumatoid_arthritis}
                  onChange={(v) => setForm({ ...form, rheumatoid_arthritis: v })} testId="qrisk3-ra" />
        <Checkbox label="SLE" checked={form.sle}
                  onChange={(v) => setForm({ ...form, sle: v })} testId="qrisk3-sle" />
        <Checkbox label="Severe mental illness" checked={form.severe_mental_illness}
                  onChange={(v) => setForm({ ...form, severe_mental_illness: v })} testId="qrisk3-smi" />
        <Checkbox label="Atypical antipsychotics" checked={form.atypical_antipsychotics}
                  onChange={(v) => setForm({ ...form, atypical_antipsychotics: v })} testId="qrisk3-apsy" />
        <Checkbox label="Oral corticosteroids" checked={form.oral_corticosteroids}
                  onChange={(v) => setForm({ ...form, oral_corticosteroids: v })} testId="qrisk3-steroid" />
        {form.sex === "male" && (
          <Checkbox label="Erectile dysfunction" checked={form.erectile_dysfunction}
                    onChange={(v) => setForm({ ...form, erectile_dysfunction: v })} testId="qrisk3-ed" />
        )}
      </div>

      <div>
        <button onClick={compute} disabled={computing} data-testid="qrisk3-compute"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
          {computing ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
          {computing ? "Computing…" : "Compute QRISK3"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="qrisk3-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="rounded-2xl p-4" style={{ background: `${band}12`, border: `1px solid ${band}40` }}
             data-testid="qrisk3-result">
          <div className="text-4xl font-heading font-bold" style={{ color: band }}>
            {result.risk_percent}<span className="text-xl text-[#A3ACA7] ml-1">%</span>
          </div>
          <div className="mt-1 text-sm font-semibold" style={{ color: band }}>
            {result.risk_band} 10-yr CVD risk
          </div>
          <div className="text-sm text-white mt-2">{result.recommendation}</div>
          <div className="text-[10px] text-[#7A8480] mt-2 uppercase tracking-wider">{result.guideline}</div>
        </div>
      )}
    </div>
  );
}

/* ---------------- AFib Workflow ---------------- */
function AfibWorkflow() {
  const [form, setForm] = useState({
    age: 72, sex: "male",
    chf: false, htn: true, diabetes: false, stroke: false, vascular: false,
    hypertension_uncontrolled: false, renal_disease: false, liver_disease: false,
    prior_bleeding: false, labile_inr: false, drugs_predisposing: false, alcohol_use: false,
  });
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState("");

  async function compute() {
    setError(""); setResult(null); setComputing(true);
    try {
      const { data } = await api.post("/specialties/cardiology/workflows/afib", {
        ...form, age: Number(form.age),
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to run AFib workflow");
    } finally { setComputing(false); }
  }

  const urgency = result?.urgency;
  const uColor = urgency ? URGENCY_COLOR[urgency] : "#A3ACA7";

  return (
    <div className="ros-card space-y-4" data-testid="afib-workflow">
      <div className="flex items-center gap-2">
        <Heart size={16} className="text-[#F87171]" />
        <div className="font-heading text-lg">AFib anticoagulation workflow</div>
      </div>
      <div className="text-xs text-[#A3ACA7]">
        Integrated CHA₂DS₂-VASc + HAS-BLED with net-benefit recommendation per ESC 2020 / AHA 2023.
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Age">
          <input type="number" min={18} max={110} value={form.age}
                 onChange={(e) => setForm({ ...form, age: e.target.value })}
                 data-testid="afib-age" className="ros-input" />
        </Field>
        <Field label="Sex">
          <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}
                  data-testid="afib-sex" className="ros-input">
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </Field>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-2">Stroke risk factors (CHA₂DS₂-VASc)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <Checkbox label="CHF / LVEF ≤40%" checked={form.chf}
                    onChange={(v) => setForm({ ...form, chf: v })} testId="afib-chf" />
          <Checkbox label="Hypertension" checked={form.htn}
                    onChange={(v) => setForm({ ...form, htn: v })} testId="afib-htn" />
          <Checkbox label="Diabetes" checked={form.diabetes}
                    onChange={(v) => setForm({ ...form, diabetes: v })} testId="afib-dm" />
          <Checkbox label="Prior stroke/TIA (2 pts)" checked={form.stroke}
                    onChange={(v) => setForm({ ...form, stroke: v })} testId="afib-stroke" />
          <Checkbox label="Vascular disease" checked={form.vascular}
                    onChange={(v) => setForm({ ...form, vascular: v })} testId="afib-vasc" />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-2">Bleeding risk factors (HAS-BLED)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <Checkbox label="Uncontrolled HTN (>160)" checked={form.hypertension_uncontrolled}
                    onChange={(v) => setForm({ ...form, hypertension_uncontrolled: v })} testId="afib-uhtn" />
          <Checkbox label="Renal disease" checked={form.renal_disease}
                    onChange={(v) => setForm({ ...form, renal_disease: v })} testId="afib-renal" />
          <Checkbox label="Liver disease" checked={form.liver_disease}
                    onChange={(v) => setForm({ ...form, liver_disease: v })} testId="afib-liver" />
          <Checkbox label="Prior major bleeding" checked={form.prior_bleeding}
                    onChange={(v) => setForm({ ...form, prior_bleeding: v })} testId="afib-bleed" />
          <Checkbox label="Labile INR" checked={form.labile_inr}
                    onChange={(v) => setForm({ ...form, labile_inr: v })} testId="afib-inr" />
          <Checkbox label="Drugs (NSAIDs/APL)" checked={form.drugs_predisposing}
                    onChange={(v) => setForm({ ...form, drugs_predisposing: v })} testId="afib-drug" />
          <Checkbox label="Alcohol ≥8 units/wk" checked={form.alcohol_use}
                    onChange={(v) => setForm({ ...form, alcohol_use: v })} testId="afib-etoh" />
        </div>
      </div>

      <div>
        <button onClick={compute} disabled={computing} data-testid="afib-compute"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
          {computing ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
          {computing ? "Running workflow…" : "Run AFib workflow"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="afib-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="rounded-2xl p-4 space-y-3" style={{ background: `${uColor}12`, border: `1px solid ${uColor}40` }}
             data-testid="afib-result">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">CHA₂DS₂-VASc</div>
              <div className="text-2xl font-heading font-bold text-white">{result.chadsvasc.score}</div>
              <div className="text-xs text-[#A3ACA7]">Annual stroke: {result.chadsvasc.annual_stroke_risk}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">HAS-BLED</div>
              <div className="text-2xl font-heading font-bold text-white">{result.hasbled.score}</div>
              <div className="text-xs text-[#A3ACA7]">{result.hasbled.risk_band} bleeding</div>
            </div>
          </div>
          <div className="pt-2 border-t border-[#272E2B]">
            <div className="font-heading text-lg" style={{ color: uColor }}>{result.decision}</div>
            <div className="text-sm text-white mt-1 leading-relaxed">{result.rationale}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#7A8480] mt-2">{result.guideline}</div>
          </div>
        </div>
      )}
    </div>
  );
}
